import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useLanguage } from './hooks/useLanguage'; // Added for URL language management
import CameraTreeVisualization from './components/CameraTreeVisualization';
import NodeDetails from './components/NodeDetails';
import AuthButton from './components/LoginButton';
import LanguageSwitcher from './components/LanguageSwitcher';
import AboutMeButton from './components/AboutMeButton';
import PremiumButton from './components/PremiumButton';
import MusicPlayer from './components/MusicPlayer';
import MySolutionsPopup from './components/MySolutionsPopup';
import CommunitySolutionsPopup from './components/CommunitySolutionsPopup';
import FlaggedCardsPopup from './components/FlaggedCardsPopup';
import RichTextEditor from './components/RichTextEditor';
import WorldMapModal from './components/WorldMapModal';
import { AuthProvider, useAuth } from './context/AuthContext';
import { fetchCompleteTree } from './services/api';
import { updateTreeNode, createTreeNode, deleteTreeNode } from './services/adminApi';
import './App.css';
import colors from './theme/colors';
import { iconMarkup } from './components/icons';

// ========================================
// ⭐ BACKGROUND CONFIGURATION
// ========================================
const BACKGROUND_CONFIG = {
  // Star quantities by size
  stars: {
    verySmall: 2000,   // radius: 0.5-0.6
    small: 400,        // radius: 0.7-0.9
    medium: 40,        // radius: 1.0-1.2
    big: 50,           // radius: 1.3-1.6
  },
  // Nebula clouds (soft glowing patches)
  nebula: {
    count: 1,          // number of nebula clouds
    opacity: 0.6,     // 0 to 1 (overall opacity)
    minSize: 100,      // minimum cloud size
    maxSize: 150,      // maximum cloud size
    color: '140,160,255', // RGB color for nebula (blueish)
  },
};

// Generate random stars based on config
const generateStarsSvg = () => {
  const { verySmall, small, medium, big } = BACKGROUND_CONFIG.stars;
  let stars = '';

  const addStar = (minR, maxR, minOpacity, maxOpacity) => {
    const x = Math.floor(Math.random() * 1600);
    const y = Math.floor(Math.random() * 900);
    const r = (minR + Math.random() * (maxR - minR)).toFixed(1);
    const opacity = (minOpacity + Math.random() * (maxOpacity - minOpacity)).toFixed(2);
    return `%3Ccircle cx='${x}' cy='${y}' r='${r}' opacity='${opacity}'/%3E`;
  };

  // Very small stars
  for (let i = 0; i < verySmall; i++) {
    stars += addStar(0.5, 0.6, 0.25, 0.35);
  }
  // Small stars
  for (let i = 0; i < small; i++) {
    stars += addStar(0.7, 0.9, 0.45, 0.6);
  }
  // Medium stars
  for (let i = 0; i < medium; i++) {
    stars += addStar(1.0, 1.2, 0.65, 0.8);
  }
  // Big stars
  for (let i = 0; i < big; i++) {
    stars += addStar(1.3, 1.6, 0.8, 0.95);
  }

  return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1600' height='900' viewBox='0 0 1600 900'%3E%3Crect width='1600' height='900' fill='none'/%3E%3Cg fill='white'%3E${stars}%3C/g%3E%3C/svg%3E")`;
};

// Generate nebula clouds as radial gradients
const generateNebulaSvg = () => {
  const { count, opacity, minSize, maxSize, color } = BACKGROUND_CONFIG.nebula;
  let defs = '';
  let clouds = '';

  for (let i = 0; i < count; i++) {
    const x = Math.floor(Math.random() * 1600);
    const y = Math.floor(Math.random() * 900);
    const size = minSize + Math.floor(Math.random() * (maxSize - minSize));
    const cloudOpacity = (0.3 + Math.random() * 0.7).toFixed(2);
    // Random 40 degree tilt left or right
    const rotation = Math.random() > 0.5 ? 40 : -40;

    defs += `%3CradialGradient id='n${i}' cx='50%25' cy='50%25' r='50%25'%3E%3Cstop offset='0%25' stop-color='rgb(${color})' stop-opacity='${cloudOpacity}'/%3E%3Cstop offset='100%25' stop-color='rgb(${color})' stop-opacity='0'/%3E%3C/radialGradient%3E`;
    clouds += `%3Cellipse cx='${x}' cy='${y}' rx='${size}' ry='${Math.floor(size * 0.6)}' fill='url(%23n${i})' transform='rotate(${rotation} ${x} ${y})'/%3E`;
  }

  return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1600' height='900' viewBox='0 0 1600 900'%3E%3Cdefs%3E${defs}%3C/defs%3E%3Cg opacity='${opacity}'%3E${clouds}%3C/g%3E%3C/svg%3E")`;
};

// Pre-generate background URLs (will be stable unless config changes)
const STARS_SVG = generateStarsSvg();
const NEBULA_SVG = generateNebulaSvg();

// Print sheet palette — same derivation TreeNode/NodeDetails use for the card
// frame, so the printout matches what the modal shows on screen
const themePrimaryHsl = colors.utils.rgbToHsl(colors.base.themePrimary);
const CARD_FRAME_COLOR = colors.utils.hslToRgb(themePrimaryHsl.h, themePrimaryHsl.s, themePrimaryHsl.l + 5);
const withTheme = (alpha) => colors.utils.withAlpha(colors.base.themePrimary, alpha);

// ========================================
// 🌈 THEMED CATEGORY COLOR HELPERS
// ========================================

/**
 * Find the category ancestor for any node
 * Categories are the top-level colored branches of the tree.
 * They could be at level 0 (no single root) or level 1 (under a single root).
 * @param {Object} node - The node to find category for
 * @param {Array} allNodes - All nodes in the tree
 * @returns {number|null} - Category node ID or null
 */
const getRootCategoryId = (node, allNodes) => {
  if (!node) return null;

  // Check if there's a single root (level 0 with no parent and children are the categories)
  const level0Nodes = allNodes.filter(n => n.level === 0);
  const hasSingleRoot = level0Nodes.length === 1;

  // If single root exists, categories are at level 1
  // Otherwise, categories are at level 0
  const categoryLevel = hasSingleRoot ? 1 : 0;

  // If this node IS a category, return its ID
  if (node.level === categoryLevel) return node.id;

  // Traverse up to find the category ancestor
  let current = node;
  while (current && current.parentId) {
    const parentId = current.parentId; // stable for this iteration
    const parent = allNodes.find(n => n.id === parentId);
    if (!parent) break;
    if (parent.level === categoryLevel) return parent.id;
    current = parent;
  }  

  return null;
};

/**
 * Get themed color for a node based on its category
 * @param {Object} node - The node
 * @param {Array} allNodes - All nodes in the tree
 * @param {Array} categoryNodes - Pre-computed list of category nodes (for index mapping)
 * @returns {string} - Hex color code
 */
const getThemedColor = (node, allNodes, categoryNodes) => {
  // Root node (level 0) gets the main theme color
  if (node.level === 0) {
    return colors.base.themePrimary;
  }

  const categoryId = getRootCategoryId(node, allNodes);
  if (!categoryId) return colors.accent.orangeLight;

  // Find the index of this category among all categories (sorted by displayOrder)
  const categoryIndex = categoryNodes.findIndex(c => c.id === categoryId);
  if (categoryIndex >= 0 && categoryIndex < colors.nodeCategories.length) {
    return colors.nodeCategories[categoryIndex];
  }

  return colors.accent.orangeLight;
};

/**
 * Process tree nodes to add themed colors based on category
 * @param {Array} nodes - Raw nodes from API
 * @returns {Array} - Nodes with themedColor added
 */
const processNodesWithThemedColors = (nodes) => {
  // Determine category level (1 if single root exists, 0 otherwise)
  const level0Nodes = nodes.filter(n => n.level === 0);
  const hasSingleRoot = level0Nodes.length === 1;
  const categoryLevel = hasSingleRoot ? 1 : 0;

  // Get category nodes sorted by displayOrder for consistent color mapping
  const categoryNodes = nodes
    .filter(n => n.level === categoryLevel)
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

  const processed = nodes.map(node => ({
    ...node,
    themedColor: getThemedColor(node, nodes, categoryNodes),
  }));

  return processed;
};

const AppContainer = styled.div`
  /* dvh tracks the mobile browser UI (URL bar) so bottom-anchored
     controls stay visible; 100vh is the fallback for older browsers */
  height: 100vh;
  height: 100dvh;
  width: 100vw;
  display: flex;
  overflow: hidden;
  position: relative;

  z-index: 0;
  isolation: isolate;

  &::before {
    content: '';
    position: fixed;
    inset: 0;
    z-index: -2;

    background-image:
      /* Stars - generated from BACKGROUND_CONFIG */
      ${STARS_SVG},

      /* Nebula - generated from BACKGROUND_CONFIG */
      ${NEBULA_SVG},

      /* Subtle blue-ish glow in the center */
      radial-gradient(ellipse at center, rgba(90, 110, 220, 0.18) 0%, transparent 55%),

      /* Night sky base */
      linear-gradient(180deg, ${colors.base.themePrimary} 0%, ${colors.base.themePrimary} 45%, ${colors.base.themePrimary} 80%, ${colors.base.themePrimary} 100%);

    background-size: cover, cover, cover, cover, cover;
    background-position: center, center, center, center, center;
    background-repeat: no-repeat;

    /* baisse le contraste global pour que la "bande" disparaisse */
    filter: contrast(1.05) saturate(1.12) brightness(0.92);
    transform: scale(1.02);
  }

  &::after {
    content: '';
    position: fixed;
    inset: 0;
    z-index: -1;
    pointer-events: none;

    background-image:
      /* grain léger */
      url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='260' height='260'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='260' height='260' filter='url(%23n)' opacity='0.16'/%3E%3C/svg%3E"),
      /* vignette */
      radial-gradient(ellipse at center, rgba(0,0,0,0) 0%, rgba(0,0,0,0.35) 65%, rgba(0,0,0,0.75) 100%),
      /* légère teinte froide */
      linear-gradient(180deg, rgba(20,40,90,0.12) 0%, rgba(0,0,0,0.00) 60%, rgba(0,0,0,0.22) 100%);

    background-size: 260px 260px, 100% 100%, 100% 100%;
    background-repeat: repeat, no-repeat, no-repeat;
    background-position: 0 0, 0 0, 0 0;

    mix-blend-mode: overlay;

    animation: drift 22s ease-in-out infinite alternate, twinkle 7s ease-in-out infinite;
    opacity: 0.95;
  }

  @keyframes drift {
    0% { transform: translate3d(-6px,-3px,0) scale(1.01); opacity: 0.92; }
    100% { transform: translate3d(6px,3px,0) scale(1.02); opacity: 1; }
  }

  @keyframes twinkle {
    0%, 100% { filter: brightness(1); }
    50% { filter: brightness(1.03); }
  }
`;



const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const HeaderWrapper = styled.div`
  position: relative;

  /* Bring header + handle above other overlays when its handle was the last clicked */
  z-index: 100;
  &:focus-within {
    z-index: 1200;
  }
`;

const Header = styled.header`
  background-color: ${colors.base.themePrimaryTransparent};
  background-image:
    linear-gradient(73deg,  transparent calc(47% - 1px), rgba(255,255,255,0.14) 47%, transparent calc(47% + 1px)),
    linear-gradient(161deg, transparent calc(38% - 1px), rgba(255,255,255,0.10) 38%, transparent calc(38% + 1px)),
    linear-gradient(210deg, transparent calc(55% - 1px), rgba(255,255,255,0.08) 55%, transparent calc(55% + 1px)),
    linear-gradient(30deg,  transparent calc(68% - 1px), rgba(255,255,255,0.06) 68%, transparent calc(68% + 1px));
  padding: 20px;
  border-bottom: 1px solid ${colors.base.themeSecondaryTransparent};
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 4px 20px ${colors.primary.darkRgba};
  
  /* Desktop: always visible */
  @media (min-width: 769px) and (min-height: 769px) {
    max-height: none;
    overflow: visible;
  }
  
  /* Mobile: keep header always visible; only buttons collapse */
  @media (max-width: 768px), (max-height: 768px) {
    padding: 15px 10px;
    /* extra room so the handle doesn't visually collide with content */
    padding-bottom: 5px;
    position: sticky;
    top: 0;
  }
`;

const HeaderToggleButton = styled.button`
  display: none; /* Hidden on desktop */
  
  /* Mobile: show toggle button as a handle OUTSIDE the header */
  @media (max-width: 768px), (max-height: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    left: 50%;
    bottom: -32px; /* half of mobile handle size so it "sticks out" */
    transform: translateX(-50%);
    width: 26px;
    height: 26px;
    background: ${colors.primary.darkRgba};
    border: 1px solid ${colors.button.aboutBorder}; /* light contour like Read Me */
    border-radius: 50%;
    color: white;
    cursor: pointer;
    z-index: 102;
    padding: 0;
    box-shadow:
      0 10px 24px rgba(0, 0, 0, 0.35),
      inset 0 1px 0 rgba(255, 255, 255, 0.12);
    transition: all 0.3s ease;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
    user-select: none;
    
    svg {
      width: 14px;
      height: 14px;
      stroke: rgba(255, 255, 255, 0.95);
      stroke-width: 2.6;
      fill: none;
      stroke-linecap: round;
      stroke-linejoin: round;
      filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5));
    }
    
    &:hover {
      background: ${colors.button.aboutBgHover};
      border-color: ${colors.border.lightHover};
      transform: translateX(-50%) scale(1.15);
      box-shadow: ${colors.effects.shadowLarge};
    }
    
    &:active {
      transform: translateX(-50%) scale(0.95);
    }
  }
`;

const HandleChevron = ({ direction = 'down' }) => {
  const rotation =
    direction === 'up' ? 180 :
    direction === 'left' ? 90 :
    direction === 'right' ? -90 : 0;

  return (
    <svg viewBox="0 0 24 24" style={{ transform: `rotate(${rotation}deg)` }} aria-hidden="true" focusable="false">
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
};

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  
  /* Desktop: buttons are positioned absolutely */
  @media (min-width: 769px) {
    gap: 0;
  }
`;

const TitleSection = styled.div`
  text-align: center;
  
  /* Leave room for corner buttons on mobile */
  @media (max-width: 768px), (max-height: 768px) {
    padding: 0 56px;
  }
`;

const Title = styled.h1`
  margin: 0;
  color: ${colors.accent.primary};
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
  text-shadow: 
    0 2px 10px ${colors.primary.darkRgba},
    0 0 20px ${colors.accent.orangeLight};
  letter-spacing: 2px;
  
  /* Mobile responsive (works for both portrait and landscape) */
  @media (max-width: 768px), (max-height: 768px) {
    font-size: 1.8rem;
    letter-spacing: 1px;
  }
`;

const Subtitle = styled.p`
  margin: 10px 0 0 0;
  color: ${colors.accent.primary};
  text-align: center;
  font-size: 1.1rem;
  font-weight: 300;
  text-shadow: 0 1px 5px ${colors.primary.darkRgba};
  
  /* Mobile responsive (works for both portrait and landscape) */
  @media (max-width: 768px), (max-height: 768px) {
    font-size: 0.95rem;
    margin: 5px 0 0 0;
  }
`;

const ButtonsContainer = styled.div`
  display: ${props => props.$isMobile ? 'flex' : 'none'};
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 5px;
  margin-top: 10px;
  opacity: ${props => props.$visible ? 1 : 0};
  max-height: ${props => props.$visible ? '120px' : '0'};
  pointer-events: ${props => props.$visible ? 'auto' : 'none'};
  transition: opacity 0.3s ease, max-height 0.3s ease;
  overflow: hidden;
  
  /* Override fixed positioning for mobile (both portrait and landscape) */
  & > * {
    position: ${props => props.$isMobile ? 'static' : 'relative'} !important;
    margin: 0 !important;
  }
`;

const DesktopOnlyButtons = styled.div`
  /* Show on desktop only */
  display: ${props => props.$isMobile ? 'none' : 'block'};
`;

const MobilePremiumWrapper = styled.div`
  /* Show on mobile (all orientations) */
  display: ${props => (props.$isMobile) ? 'block' : 'none'};
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 101;

  /* Override default PremiumContainer fixed positioning */
  & > * {
    position: static !important;
    top: auto !important;
    right: auto !important;
  }
`;

const MobileMusicWrapper = styled.div`
  /* Show on mobile (all orientations) */
  display: ${props => (props.$isMobile) ? 'block' : 'none'};
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 101;

  /* Override default MusicContainer fixed positioning */
  & > * {
    position: static !important;
    top: auto !important;
    right: auto !important;
  }
`;

const TreeContainer = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
`;

const Modal = styled(motion.div)`
  background: ${colors.primary.darkRgba};
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 20px 40px ${colors.primary.darkRgba};
  z-index: 11000;
  width: 90vw;
  max-width: 600px;
  max-height: 85vh;
  overflow-y: auto;
  color: white;
  box-sizing: border-box;
  
  /* S'assurer que le modal reste dans les limites de l'écran */
  @media (max-width: 768px) {
    width: 95vw;
    padding: 20px;
    max-height: 90vh;
  }
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${colors.background.overlay};
  z-index: 10999;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  color: ${colors.text.light};
  font-weight: 600;
`;

const Input = styled.input`
  background: ${colors.background.transparent};
  border: 1px solid ${colors.border.light};
  border-radius: 8px;
  padding: 12px;
  color: white;
  font-size: 1rem;
  
  &::placeholder {
    color: ${colors.text.lightSecondary};
  }
  
  &:focus {
    outline: none;
    border-color: ${colors.accent.orangeLight};
  }
`;

const TextArea = styled.textarea`
  background: ${colors.background.transparent};
  border: 1px solid ${colors.border.light};
  border-radius: 8px;
  padding: 12px;
  color: white;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  
  &::placeholder {
    color: ${colors.text.lightSecondary};
  }
  
  &:focus {
    outline: none;
    border-color: ${colors.accent.orangeLight};
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const ActionButton = styled.button`
  background: ${props => props.danger ? colors.adminActive : colors.accent.orangeLight};
  border: none;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.danger ? colors.adminActiveBgHover : colors.accent.orangeLight};
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  color: white;
  font-size: 1.5rem;
  text-align: center;
`;

function AppContent() {
  const { user, isAuthenticated, getUserBookmarks } = useAuth();
  const { t } = useTranslation();
  const { isLanguageLoaded } = useLanguage(); // Added for language management
  const [allNodes, setAllNodes] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingNode, setEditingNode] = useState(null);
  const [isCreatingChild, setIsCreatingChild] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    specialistInfo: '',
    shortDescription: '',
    trialDuration: '',
    sources: '',
    level: 0,
    displayOrder: 1,
    parentId: null,
    isLeaf: false,
    colorCode: colors.accent.orangeLight,
    iconName: '',
    aiGenerated: false
  });
  const [isFilteringFavorites, setIsFilteringFavorites] = useState(false);
  const [isFilteringProgress, setIsFilteringProgress] = useState(false);
  const [filteredNodes, setFilteredNodes] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [completedNodes, setCompletedNodes] = useState(new Set());
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const cameraTreeRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileFilteredNodes, setMobileFilteredNodes] = useState([]);
  const [isLandscape, setIsLandscape] = useState(false);
  const [headerButtonsVisible, setHeaderButtonsVisible] = useState(true);
  const headerButtonsManuallyToggledRef = useRef(false);

  // Progressive navigation: track which nodes are expanded
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  // eslint-disable-next-line no-unused-vars
  const [isProgressiveMode, setIsProgressiveMode] = useState(true);

  // World map modal state for admin flip stats
  const [worldMapModal, setWorldMapModal] = useState({
    isOpen: false,
    nodeId: null,
    nodeTitle: '',
    type: 'anonymous'
  });

  // Detect mobile and landscape on resize
  useEffect(() => {
    const handleResize = () => {
      // Use the smaller dimension to detect mobile (works for both orientations)
      const minDimension = Math.min(window.innerWidth, window.innerHeight);
      const mobile = minDimension <= 768;
      const landscape = window.innerHeight < window.innerWidth;
      
      setIsMobile(mobile);
      setIsLandscape(mobile && landscape);
      
    };
    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  // Default behavior: show buttons in mobile portrait, collapse in mobile landscape.
  // Keep user's manual toggle stable during normal layout changes.
  useEffect(() => {
    if (!isMobile) {
      headerButtonsManuallyToggledRef.current = false;
      return;
    }
    if (headerButtonsManuallyToggledRef.current) return;
    setHeaderButtonsVisible(!isLandscape);
  }, [isMobile, isLandscape]);

  // Vérifier si l'utilisateur est admin
  const isOnWrangler = window.location.port === '8787';

  // Check if we're in development on React dev server (port 3000 only)
  // Port 8787 (Wrangler) should use normal admin logic from database
  const isDevelopment = (window.location.hostname === 'localhost' ||
                         window.location.hostname === '127.0.0.1' ||
                         window.location.hostname === '[::1]') &&
                        window.location.port === '3000';

  // Development override: allow toggling admin mode for testing (port 3000 only)
  // Use localStorage or URL parameter ?forceAdmin=true or ?forceAdmin=false
  const [adminOverride, setAdminOverride] = useState(() => {
    // Only allow override on port 3000 (React dev server)
    if (!isDevelopment) return null;

    const urlParams = new URLSearchParams(window.location.search);
    const urlOverride = urlParams.get('forceAdmin');
    if (urlOverride !== null) {
      const override = urlOverride === 'true';
      localStorage.setItem('adminOverride', override.toString());
      return override;
    }
    const stored = localStorage.getItem('adminOverride');
    return stored !== null ? stored === 'true' : null;
  });

  const isAdmin = adminOverride !== null ? adminOverride : (user?.isAdmin === true);

  // Toggle admin mode for testing
  const toggleAdminMode = () => {
    const newValue = !isAdmin;
    setAdminOverride(newValue);
    localStorage.setItem('adminOverride', newValue.toString());
  };

  // Detect first-time visitors and show welcome modal
  useEffect(() => {
    const hasSeenWelcome = localStorage.getItem('hasSeenWelcome');
    
    if (isAuthenticated && user) {
      // Logged-in user: check database welcome flag
      if (user.welcome === true) {
        console.log('🎉 First time logged-in user (welcome=1), showing welcome modal');
        setShowWelcomeModal(true);
      } else {
        setShowWelcomeModal(false);
      }
    } else {
      // Anonymous user: check localStorage
      if (!hasSeenWelcome) {
        console.log('👋 First time anonymous visitor, showing welcome modal');
        setShowWelcomeModal(true);
      } else {
        setShowWelcomeModal(false);
      }
    }
  }, [isAuthenticated, user]);

  // Fonction pour activer le premium après un paiement réussi
  const activatePremiumAfterPayment = useCallback(async (transactionId, userEmail) => {
    if (!transactionId || !userEmail) return;

    try {
      console.log('🎉 Activating premium for payment:', transactionId, userEmail);
      
      const response = await fetch('/api/payments/activate-premium', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          transactionId,
          userEmail
        })
      });

      const result = await response.json();

      if (response.ok) {
        console.log('✅ Premium activated successfully:', result);
        // Do not reload; show a subtle toast/info and let user re-login when they wish
        // Optionally update local user cache if present
      } else {
        console.error('❌ Failed to activate premium:', result.error);
      }
    } catch (error) {
      console.error('❌ Error activating premium:', error);
    }
  }, []);

  // Détecter les paiements réussis via l'URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const transactionId = urlParams.get('tx');
    
    if (paymentStatus === 'success' && transactionId && user?.email) {
      console.log('🎉 Payment success detected:', { transactionId, userEmail: user.email });
      
      // Skip immediate activation to avoid reload; inform user instead
      // activatePremiumAfterPayment(transactionId, user.email);
      
      // Nettoyer l'URL pour éviter les activations multiples
      const newUrl = new URL(window.location);
      newUrl.searchParams.delete('payment');
      newUrl.searchParams.delete('tx');
      window.history.replaceState({}, '', newUrl.toString());
    }
  }, [user?.email, activatePremiumAfterPayment]);
  
  console.log('🔐 Admin check:', { 
    port: window.location.port, 
    isOnWrangler, 
    userEmail: user?.email, 
    isAdmin 
  });

  // Refresh tree data only (for admin operations) without moving camera
  const refreshTreeData = useCallback(async () => {
    try {
      // Just fetch and update tree nodes without changing camera or selection
      const completeTree = await fetchCompleteTree();
      const nodesWithThemedColors = processNodesWithThemedColors(completeTree);
      setAllNodes(nodesWithThemedColors);
    } catch (err) {
      console.error('Failed to refresh tree data:', err);
    }
  }, []);

  const initializeApp = useCallback(async () => {
    try {
      setLoading(true);

      // Charger l'arbre complet pour la visualisation 2D
      const completeTree = await fetchCompleteTree();
      const nodesWithThemedColors = processNodesWithThemedColors(completeTree);

      setAllNodes(nodesWithThemedColors);

      // Detect mobile using the smaller dimension (works in both orientations)
      const minDimension = typeof window !== 'undefined' ? Math.min(window.innerWidth, window.innerHeight) : 768;
      const isMobileDevice = minDimension <= 768;

      // Sélectionner un noeud initial
      const level1Nodes = completeTree.filter(node => node.level === 1);
      const level2Nodes = completeTree.filter(node => node.level === 2);

      if (isMobileDevice) {
        // Mobile: Start with level 1 to avoid showing all level 2 siblings
        if (level1Nodes.length > 0) {
          const initialNode = level1Nodes[0];
          setSelectedNodeId(initialNode.id);
        }
      } else {
        // Desktop: Start with level 2 for more context
        if (level2Nodes.length > 0) {
          const initialNode = level2Nodes[Math.floor(level2Nodes.length / 2)];
          setSelectedNodeId(initialNode.id);
        } else if (level1Nodes.length > 0) {
          const initialNode = level1Nodes[0];
          setSelectedNodeId(initialNode.id);
        }
      }
    } catch (err) {
      console.error('Failed to initialize app:', err);
      setError(t('tree.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    initializeApp();
  }, [initializeApp]);

  // Fetch completed nodes when user is authenticated
  useEffect(() => {
    const fetchCompletedNodes = async () => {
      if (isAuthenticated) {
        try {
          const response = await fetch('/api/auth/progress/all');
          const progressData = await response.json();
          const completedIds = progressData.completed || [];
          setCompletedNodes(new Set(completedIds));
        } catch (error) {
          console.error('Failed to fetch completed nodes:', error);
        }
      } else {
        setCompletedNodes(new Set());
      }
    };

    fetchCompletedNodes();
  }, [isAuthenticated, user]);

  // Handle Escape key to close edit modal
  useEffect(() => {
    if (!showEditModal) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setShowEditModal(false);
        setEditingNode(null);
        setIsCreatingChild(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showEditModal]);

  // Navigation by nodeId (used by MySolutionsPopup and CommunitySolutionsPopup)
  const handleNavigateToNode = useCallback((nodeId) => {
    if (cameraTreeRef.current) {
      cameraTreeRef.current.navigateToNode(nodeId);
    }
  }, []);

  // Navigation: sélectionne le nœud et centre la caméra
  const handleNodeClick = (node) => {
    setSelectedNodeId(node.id);

    // On mobile, focus after state update to ensure smooth transition
    if (isMobile) {
      setTimeout(() => {
        if (cameraTreeRef.current) {
          cameraTreeRef.current.focusOnNode(node.id);
        }
      }, 50);
    }
  };

  // Détails: affiche juste la fiche sans changer la navigation
  const handleShowDetails = (node) => {
    setSelectedNode(node);
  };

  // Handle progress toggle for real-time XP bar update
  const handleProgressToggle = (nodeId, isCompleted) => {
    setCompletedNodes(prev => {
      const newSet = new Set(prev);
      if (isCompleted) {
        newSet.add(nodeId);
      } else {
        newSet.delete(nodeId);
      }
      return newSet;
    });
  };

  // Handle showing flip stats world map modal (admin only)
  const handleShowStats = useCallback((nodeId, nodeTitle, type) => {
    setWorldMapModal({
      isOpen: true,
      nodeId,
      nodeTitle,
      type
    });
  }, []);

  // Fonction pour créer un nœud enfant
  const handleCreateChild = (parentNode) => {
    setEditingNode(null);
    setIsCreatingChild(true);
    setFormData({
      title: '',
      description: '',
      specialistInfo: '',
      shortDescription: '',
      trialDuration: '',
      level: parentNode.level + 1,
      displayOrder: 1,
      parentId: parentNode.id,
      isLeaf: true,
      colorCode: parentNode.colorCode || colors.accent.orangeLight,
      iconName: ''
    });
    setShowEditModal(true);
  };

  // Édition admin
  const handleEditNode = (node) => {
    setEditingNode(node);
    setFormData({
      title: node.title || '',
      description: node.description || '',
      specialistInfo: node.specialistInfo || '',
      shortDescription: node.shortDescription || '',
      trialDuration: node.trialDuration || '',
      sources: node.sources || '',
      level: node.level || 0,
      displayOrder: node.displayOrder || 1,
      parentId: node.parentId || null,
      isLeaf: node.isLeaf || false,
      colorCode: node.colorCode || colors.accent.orangeLight,
      iconName: node.iconName || '',
      aiGenerated: node.aiGenerated || false
    });
    setShowEditModal(true);
  };

  // Fonction pour supprimer un nœud
  const handleDeleteNode = async (node) => {
    const confirmMessage = t('admin.deleteConfirmation', { title: node.title });

    if (window.confirm(confirmMessage)) {
      try {
        await deleteTreeNode(node.id);
        // Refresh tree data without moving camera
        await refreshTreeData();
      } catch (error) {
        console.error('❌ Failed to delete node:', error);
        alert(t('admin.failedToDelete') + ': ' + error.message);
      }
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    try {
      if (isCreatingChild) {
        // Créer un nouveau nœud enfant
        await createTreeNode(formData);
      } else if (editingNode) {
        // Mettre à jour un nœud existant
        await updateTreeNode(editingNode.id, formData);
      }

      setShowEditModal(false);
      setEditingNode(null);
      setIsCreatingChild(false);

      // Refresh tree data without moving camera
      await refreshTreeData();
    } catch (error) {
      console.error('❌ Failed to save node:', error);
      alert(t('admin.failedToSave') + ': ' + error.message);
    }
  };



  // Fonctions premium
  const handleFilterFavorites = useCallback(async () => {
    if (isFilteringFavorites) {
      setIsFilteringFavorites(false);
      setFilteredNodes([]);
      setFavoriteIds([]);
      
      // Recentrer sur l'arbre complet quand on désactive le filtre
      setTimeout(() => {
        if (cameraTreeRef.current && cameraTreeRef.current.resetToFirstNode) {
          cameraTreeRef.current.resetToFirstNode();
        }
      }, 100);
    } else {
      const bookmarks = await getUserBookmarks();
      const bookmarkedIds = bookmarks.map(b => b.nodeId);
      
      // Créer un Set pour stocker tous les IDs à inclure
      const nodesToInclude = new Set();
      
      // Pour chaque favori, ajouter tous ses parents
      bookmarkedIds.forEach(favoriteId => {
        nodesToInclude.add(favoriteId); // Ajouter le favori lui-même
        
        // Trouver et ajouter tous les parents
        let current = allNodes.find(node => node.id === favoriteId);
        while (current && current.parentId) {
          const parentId = current.parentId;
          nodesToInclude.add(parentId);
          current = allNodes.find(node => node.id === parentId);
        }
      });
      
      // Filtrer les nodes pour inclure seulement ceux dans le Set
      const filtered = allNodes.filter(node => nodesToInclude.has(node.id));
      
      setFilteredNodes(filtered);
      setFavoriteIds(bookmarkedIds); // Stocker les IDs des vrais favoris
      setIsFilteringFavorites(true);
      setIsFilteringProgress(false);
      
      // Recentrer sur le premier node après le filtrage
      setTimeout(() => {
        if (cameraTreeRef.current && cameraTreeRef.current.resetToFirstNode) {
          cameraTreeRef.current.resetToFirstNode();
        }
      }, 100);
    }
  }, [isFilteringFavorites, allNodes, getUserBookmarks]);

  // Mobile filtering: filter tree based on selected node (like favorites filtering)
  useEffect(() => {
    if (!isMobile || isFilteringFavorites || isFilteringProgress) {
      setMobileFilteredNodes([]);
      return;
    }

    if (!selectedNodeId) {
      // No selection: show first 2 levels by default
      const filtered = allNodes.filter(n => n.level === 0 || n.level === 1);
      setMobileFilteredNodes(filtered);
      return;
    }

    const selectedNode = allNodes.find(n => n.id === selectedNodeId);
    if (!selectedNode) return;

    const visibleNodeIds = new Set();

    // 1. Add selected node
    visibleNodeIds.add(selectedNode.id);

    // 2. Add siblings of selected node
    const selectedSiblings = allNodes.filter(n => n.parentId === selectedNode.parentId);
    selectedSiblings.forEach(sibling => visibleNodeIds.add(sibling.id));

    // 3. Add children of selected node ONLY if expanded (recursively)
    const addExpandedChildren = (nodeId) => {
      if (!expandedNodes.has(nodeId)) return; // Only if expanded
      const nodeChildren = allNodes.filter(n => n.parentId === nodeId);
      nodeChildren.forEach(child => {
        visibleNodeIds.add(child.id);
        // Recursively add children if this child is also expanded
        addExpandedChildren(child.id);
      });
    };
    
    // ONLY add children for the selected node, NOT for siblings
    // This ensures that expanding a node closes children of other siblings
    addExpandedChildren(selectedNode.id);

    // 5. Add direct parent and its siblings
    if (selectedNode.parentId) {
      const parent = allNodes.find(n => n.id === selectedNode.parentId);
      if (parent) {
        visibleNodeIds.add(parent.id);
        const parentSiblings = allNodes.filter(n => n.parentId === parent.parentId);
        parentSiblings.forEach(sibling => visibleNodeIds.add(sibling.id));
      }
    }

    // 6. Add ancestor chain to root
    let currentNode = selectedNode;
    while (currentNode.parentId) {
      const parentId = currentNode.parentId;
      const parent = allNodes.find(n => n.id === parentId);
      if (!parent) break;
      visibleNodeIds.add(parent.id);
      currentNode = parent;
    }

    const filtered = allNodes.filter(n => visibleNodeIds.has(n.id));
    setMobileFilteredNodes(filtered);
  }, [isMobile, selectedNodeId, allNodes, isFilteringFavorites, isFilteringProgress, expandedNodes]);

  // Separate effect: Clean up expanded nodes when mobile filtered nodes change
  useEffect(() => {
    if (!isMobile || mobileFilteredNodes.length === 0) return;

    // Get IDs of currently visible nodes
    const visibleIds = new Set(mobileFilteredNodes.map(n => n.id));

    // Remove expanded state from nodes that:
    // 1. Are no longer visible, OR
    // 2. Are visible but have NO visible children (arrow should reset to ▼)
    setExpandedNodes(prev => {
      const newSet = new Set();
      let hasChanges = false;
      
      prev.forEach(nodeId => {
        // Check if node is visible
        if (!visibleIds.has(nodeId)) {
          hasChanges = true;
          return; // Node not visible, don't keep expanded state
        }
        
        // Node is visible, but check if any of its children are also visible
        const hasVisibleChildren = allNodes.some(n => 
          n.parentId === nodeId && visibleIds.has(n.id)
        );
        
        if (hasVisibleChildren) {
          newSet.add(nodeId); // Keep expanded if children are visible
        } else {
          hasChanges = true; // Reset arrow to ▼ if no children visible
        }
      });
      
      return hasChanges ? newSet : prev;
    });
  }, [isMobile, mobileFilteredNodes, allNodes]);

  const handleFilterProgress = useCallback(async () => {
    if (isFilteringProgress) {
      setIsFilteringProgress(false);
      setFilteredNodes([]);
      
      // Recentrer sur l'arbre complet quand on désactive le filtre
      setTimeout(() => {
        if (cameraTreeRef.current && cameraTreeRef.current.resetToFirstNode) {
          cameraTreeRef.current.resetToFirstNode();
        }
      }, 100);
    } else {
      // Récupérer les nodes avec progression depuis l'API
      const response = await fetch('/api/auth/progress/all');
      const progressData = await response.json();
      const completedIds = progressData.completed || [];
      
      // Créer un Set pour inclure les nodes terminés ET leurs parents
      const nodesToInclude = new Set();
      completedIds.forEach(completedId => {
        nodesToInclude.add(completedId);
        let current = allNodes.find(node => node.id === completedId);
        while (current && current.parentId) {
          const parentId = current.parentId;
          nodesToInclude.add(parentId);
          current = allNodes.find(node => node.id === parentId);
        }
      });
      
      // Filtrer les nodes pour inclure seulement ceux dans le Set
      const filtered = allNodes.filter(node => nodesToInclude.has(node.id));
      
      setFilteredNodes(filtered);
      setFavoriteIds(completedIds); // Stocker les IDs des vrais nodes terminés
      setIsFilteringProgress(true);
      setIsFilteringFavorites(false);
      
      // Recentrer sur le premier node après le filtrage
      setTimeout(() => {
        if (cameraTreeRef.current && cameraTreeRef.current.resetToFirstNode) {
          cameraTreeRef.current.resetToFirstNode();
        }
      }, 100);
    }
  }, [isFilteringProgress, allNodes]);

  const printNodes = useCallback((nodes, title) => {
    const printWindow = window.open('', '_blank');

    // Get parent path for breadcrumb
    const getParentPath = (node) => {
      const path = [];
      let current = node;

      while (current && current.parentId) {
        const parentId = current.parentId;
        const parent = allNodes.find(n => n.id === parentId);
        if (parent) {
          if (parent.level !== 0) {
            path.unshift(parent.title || `Node ${parent.id}`);
          }
          current = parent;
        } else {
          break;
        }
      }

      // Returned as segments so the sheet can render chevrons between them,
      // matching the breadcrumb in NodeDetails
      return path;
    };

    // Render markdown to HTML (same as NodeDetails)
    const renderMarkdown = (text) => {
      if (!text) return '';
      const src = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
      const inline = (s) => s
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>');
      // A run of text lines becomes one <p>; a line that is only **...** becomes a
      // block heading; a single newline inside a paragraph is a <br />
      const renderTextLines = (lines) => {
        const out = [];
        let para = [];
        const flushPara = () => {
          if (para.length) { out.push('<p>' + para.map(inline).join('<br />') + '</p>'); para = []; }
        };
        lines.forEach((line) => {
          const heading = line.trim().match(/^\*\*(.+?)\*\*$/);
          if (heading) {
            flushPara();
            out.push('<strong class="desc-title">' + inline(heading[1]) + '</strong>');
          } else {
            para.push(line);
          }
        });
        flushPara();
        return out.join('');
      };
      // Split on blank lines into blocks (keeps headings and paragraphs apart)
      let html = src.split(/\n{2,}/).map((block) => {
        if (!block.trim()) return '';
        const lines = block.replace(/^\n+|\n+$/g, '').split('\n');
        if (lines.every((l) => /^- /.test(l.trim()))) {
          return '<ul><li>' + lines.map((l) => inline(l.trim().replace(/^- /, ''))).join('</li><li>') + '</li></ul>';
        }
        if (lines.every((l) => /^\d+\. /.test(l.trim()))) {
          return '<ol><li>' + lines.map((l) => inline(l.trim().replace(/^\d+\. /, ''))).join('</li><li>') + '</li></ol>';
        }
        return renderTextLines(lines);
      }).join('');
      // Same swap as NodeDetails: descriptions carry a ⚠️ emoji, render it with
      // the site's stroke icon instead
      html = html.replace(/⚠️?/g, iconMarkup('warning', 11));
      return html;
    };

    // Render semicolon-separated text as list
    const renderSemicolonList = (text) => {
      if (!text) return '';
      const items = text.split(';').map(item => item.trim()).filter(item => item.length > 0);
      if (items.length === 0) return '';
      return '<ul>' + items.map(item => '<li>' + item + '</li>').join('') + '</ul>';
    };

    // Render source links from format: (title,url); (title2,url2)
    const renderSourceLinks = (text) => {
      if (!text) return '';
      // The url may itself contain parentheses: the closing one is the last before the next
      // entry (;), the next opening parenthesis or the end of the string
      const linkPattern = /\(([^,]+),(.+?)\)(?=\s*(?:;|\(|$))/g;
      const matches = [...text.matchAll(linkPattern)];
      if (matches.length === 0) return '';
      return '<ul>' + matches.map(match => {
        const linkTitle = match[1].trim();
        const url = match[2].trim();
        return '<li><a href="' + url + '" target="_blank">' + linkTitle + '</a></li>';
      }).join('') + '</ul>';
    };

    // Get a color for the node based on its category/color code
    const getNodeColor = (node) => {
      if (node.colorCode) return node.colorCode;
      if (node.themedColor) return node.themedColor;
      return '#4A90E2'; // default blue
    };

    // Build node HTML
    const buildNodeHtml = (node, index) => {
      const parentPath = getParentPath(node);
      const nodeColor = getNodeColor(node);

      let html = '<div class="node">';

      // Top accent bar
      html += '<div class="node-accent" style="background: ' + nodeColor + ';"></div>';

      // Header with gradient background
      html += '<div class="node-header">';
      html += '<div class="node-badge" style="background: ' + nodeColor + ';">Lv.' + (node.level || 1) + '</div>';
      html += '<div class="node-title">' + (node.title || 'Untitled') + '</div>';
      if (parentPath.length > 0) {
        html += '<div class="parent-path">' +
          parentPath.map(seg => '<span>' + seg + '</span>').join('<span class="path-sep">›</span>') +
          '</div>';
      }
      html += '</div>';

      // Content area
      html += '<div class="node-content">';

      // Parent cards no longer carry a long description (it is unreachable on the site since
      // the info corner became leaf-only), so fall back to the short one — without it a parent
      // would print with no text at all. The AI badge stays tied to the actual AI summary:
      // a parent's short description is written from its children, not from sources.
      const hasSummary = !!(node.description && node.description.trim());
      const printText = hasSummary ? node.description : (node.shortDescription || '');

      if (printText.trim()) {
        html += '<div class="section">';
        html += '<div class="section-title">' + iconMarkup('clipboard', 12) + ' ' + t('tree.description');
        if (node.aiGenerated && hasSummary) {
          html += '<span class="ai-badge">' + iconMarkup('warning', 9) + ' ' + t('tree.aiGeneratedPrint') + '</span>';
        }
        html += '</div>';
        html += '<div class="section-content">' + renderMarkdown(printText) + '</div></div>';
      }

      if (node.specialistInfo && node.specialistInfo.trim()) {
        html += '<div class="info-box">';
        html += '<div class="info-box-title">' + iconMarkup('stethoscope', 11) + ' ' + t('tree.whoCanHelp') + '</div>';
        html += '<div class="info-box-content">' + renderSemicolonList(node.specialistInfo) + '</div></div>';
      }

      if (node.trialDuration && node.trialDuration.trim()) {
        html += '<div class="info-box">';
        html += '<div class="info-box-title">' + iconMarkup('hourglass', 11) + ' ' + t('tree.trialDuration') + '</div>';
        html += '<div class="info-box-content">' + node.trialDuration + '</div></div>';
      }

      if (node.sources && node.sources.trim()) {
        html += '<div class="info-box">';
        html += '<div class="info-box-title">' + iconMarkup('books', 11) + ' Sources</div>';
        html += '<div class="info-box-content">' + renderSourceLinks(node.sources) + '</div></div>';
      }

      html += '</div>'; // end node-content

      // Footer
      html += '<div class="node-footer">';
      html += '<span class="footer-text">Fuck Insomnia</span>';
      html += '<span class="footer-id">#' + (node.id || index + 1) + '</span>';
      html += '</div>';

      html += '</div>';
      return html;
    };

    const nodesHtml = nodes.map((node, index) => buildNodeHtml(node, index)).join('');

    const html = '<!DOCTYPE html><html><head><title>' + title + '</title><style>' +
      '@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap");' +
      '* { box-sizing: border-box; }' +
      'html { background: ' + colors.base.themePrimary + '; }' +
      'body { font-family: "Inter", Arial, sans-serif; padding: 25px; line-height: 1.5; color: #333; background-image: ' + STARS_SVG + ', ' + NEBULA_SVG + ', radial-gradient(ellipse at center, rgba(90, 110, 220, 0.18) 0%, transparent 55%), linear-gradient(180deg, ' + colors.base.themePrimary + ' 0%, ' + colors.base.themePrimary + ' 100%); background-size: cover, cover, cover, cover; background-position: center, center, center, center; background-repeat: no-repeat; background-attachment: fixed; min-height: 100vh; }' +
      'h1 { color: white; margin-bottom: 30px; text-align: center; font-size: 28px; font-weight: 700; text-shadow: 0 2px 4px rgba(0,0,0,0.2); }' +
      '.nodes-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; }' +

      // Card base
      '.node { background: white; border-radius: 16px; overflow: hidden; box-shadow: 0 10px 40px rgba(0,0,0,0.15), 0 2px 10px rgba(0,0,0,0.1); break-inside: avoid; page-break-inside: avoid; position: relative; display: flex; flex-direction: column; }' +

      // Top accent bar
      '.node-accent { height: 6px; width: 100%; }' +

      // Header
      '.node-header { padding: 16px 16px 12px 16px; background: linear-gradient(180deg, #fafbfc 0%, #f0f2f5 100%); border-bottom: 1px solid #e8eaed; position: relative; }' +
      '.node-badge { position: absolute; top: 12px; right: 12px; color: white; font-size: 9px; font-weight: 700; padding: 3px 8px; border-radius: 10px; text-transform: uppercase; letter-spacing: 0.5px; }' +
      '.node-title { font-size: 15px; font-weight: 700; color: #1a1a2e; margin: 0 0 8px 0; line-height: 1.3; padding-right: 50px; }' +
      // Breadcrumb — mirrors NodePath in NodeDetails
      '.parent-path { display: inline-flex; flex-wrap: wrap; align-items: center; gap: 4px; background: ' + withTheme(0.05) + '; color: ' + withTheme(0.68) + '; padding: 3px 9px; border-radius: 20px; font-size: 9px; font-weight: 500; border: 1px solid ' + withTheme(0.1) + '; }' +
      '.path-sep { color: ' + withTheme(0.32) + '; font-weight: 400; }' +

      // Content area
      '.node-content { padding: 14px 16px; flex: 1; }' +

      // Sections
      '.section { margin-bottom: 12px; }' +
      '.section-title { font-size: 11px; font-weight: 600; color: #374151; margin: 0 0 8px 0; display: flex; align-items: center; gap: 6px; border-bottom: 1px solid #f0f0f0; padding-bottom: 6px; }' +
      '.section-content { color: #4b5563; line-height: 1.5; font-size: 10px; text-align: justify; hyphens: auto; }' +
      '.section-content p { margin: 0 0 6px; }' +
      '.section-content p:last-child { margin-bottom: 0; }' +
      '.section-content strong.desc-title { display: block; margin: 8px 0 3px; }' +
      '.section-content strong.desc-title:first-child { margin-top: 0; }' +
      '.section-content ul, .section-content ol { margin: 6px 0; padding-left: 18px; }' +
      '.section-content li { margin: 3px 0; }' +
      '.section-content a { color: ' + CARD_FRAME_COLOR + '; text-decoration: none; font-weight: 500; }' +
      '.section-content a:hover { text-decoration: underline; }' +

      // Info boxes — one neutral treatment for all three, like SpecialistInfo
      // in the modal. The previous green/amber/blue coding was off-palette and
      // implied a severity the sections don't have.
      '.info-box { background: ' + colors.base.themeTertiary + '; border: 1px solid rgba(0,0,0,0.08); border-left: 4px solid ' + CARD_FRAME_COLOR + '; padding: 12px; border-radius: 10px; margin-top: 10px; }' +
      '.info-box-title { font-size: 10px; font-weight: 600; margin: 0 0 8px 0; display: flex; align-items: center; gap: 6px; color: ' + CARD_FRAME_COLOR + '; }' +
      '.info-box-content { color: #495057; font-size: 9px; line-height: 1.5; }' +
      '.info-box-content ul { margin: 0; padding: 0; list-style: none; }' +
      '.info-box-content li { margin: 0 0 5px 0; padding-left: 14px; position: relative; }' +
      '.info-box-content li:before { content: "›"; position: absolute; left: 0; font-weight: 600; font-size: 11px; color: ' + withTheme(0.45) + '; }' +
      '.info-box-content a { color: ' + CARD_FRAME_COLOR + '; text-decoration: none; font-weight: 500; }' +

      // AI badge — mirrors AiBadge in NodeDetails
      '.ai-badge { display: inline-flex; align-items: center; gap: 3px; background: rgba(0,0,0,0.035); color: #8a9099; font-size: 7px; font-weight: 500; padding: 2px 6px; border-radius: 10px; border: 1px solid rgba(0,0,0,0.09); margin-left: 6px; }' +

      // Footer
      '.node-footer { padding: 10px 16px; background: #f8fafc; border-top: 1px solid #e8eaed; display: flex; justify-content: space-between; align-items: center; }' +
      '.footer-text { font-size: 8px; font-weight: 600; color: #9ca3af; text-transform: uppercase; letter-spacing: 1px; }' +
      '.footer-id { font-size: 9px; font-weight: 700; color: #6b7280; }' +

      // Print styles
      '@media print { ' +
        'body { padding: 15px; background: white !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }' +
        'h1 { color: ' + colors.base.themePrimary + '; text-shadow: none; }' +
        '.nodes-grid { grid-template-columns: repeat(2, 1fr); gap: 15px; }' +
        '.node { break-inside: avoid; page-break-inside: avoid; box-shadow: 0 1px 3px rgba(0,0,0,0.1); border: 1px solid #e5e7eb; }' +
      '}' +
      '</style></head><body>' +
      '<h1>' + title + '</h1>' +
      '<div class="nodes-grid">' + nodesHtml + '</div>' +
      '</body></html>';
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  }, [allNodes, t]);

  const handlePrintFavorites = useCallback(async () => {
    const bookmarks = await getUserBookmarks();
    const bookmarkedIds = bookmarks.map(b => b.nodeId);
    const toPrint = allNodes.filter(node => bookmarkedIds.includes(node.id));
    printNodes(toPrint, 'My Favorite Ideas');
  }, [allNodes, getUserBookmarks, printNodes]);

  const handlePrintProgress = useCallback(async () => {
    const response = await fetch('/api/auth/progress/all');
    const progressData = await response.json();
    const completedIds = progressData.completed || [];
    const toPrint = allNodes.filter(node => completedIds.includes(node.id));
    printNodes(toPrint, 'My Completed Ideas');
  }, [allNodes, printNodes]);

  // Progressive navigation: toggle node expansion
  const handleToggleNodeExpansion = useCallback((nodeId) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      const wasExpanded = newSet.has(nodeId);

      if (wasExpanded) {
        // Collapsing: remove this node and all its descendants from expanded set
        newSet.delete(nodeId);

        // Find all descendants recursively
        const findDescendants = (parentId) => {
          const children = allNodes.filter(node => node.parentId === parentId);
          children.forEach(child => {
            newSet.delete(child.id);
            findDescendants(child.id); // Recursive call for grandchildren
          });
        };

        findDescendants(nodeId);
      } else {
        // Expanding: just add this node
        newSet.add(nodeId);
      }
      return newSet;
    });

    // After state update, focus on the node so user can see the children
    // Delay ensures React has updated the tree before camera moves
    setTimeout(() => {
      if (cameraTreeRef.current) {
        cameraTreeRef.current.focusOnNode(nodeId);
      }
    }, 50);
  }, [allNodes]);

  // Progressive navigation: calculate visible nodes
  const calculateProgressiveNodes = useCallback(() => {
    if (!isProgressiveMode) {
      return allNodes;
    }

    const visibleNodeIds = new Set();

    // Always include level 0 and 1
    allNodes.forEach(node => {
      if (node.level === 0 || node.level === 1) {
        visibleNodeIds.add(node.id);
      }
    });

    // Helper function to add ancestors
    const addAncestors = (nodeId) => {
      const node = allNodes.find(n => n.id === nodeId);
      if (node && node.parentId) {
        visibleNodeIds.add(node.parentId);
        addAncestors(node.parentId);
      }
    };

    // Include children of expanded nodes
    expandedNodes.forEach(expandedNodeId => {
      const children = allNodes.filter(node => node.parentId === expandedNodeId);
      children.forEach(child => {
        visibleNodeIds.add(child.id);
        // Also include all ancestors to maintain tree structure
        addAncestors(child.id);
      });
    });

    return allNodes.filter(node => visibleNodeIds.has(node.id));
  }, [allNodes, expandedNodes, isProgressiveMode]);

  // Ne pas afficher l'app tant que la langue n'est pas chargée
  if (!isLanguageLoaded) {
    return (
      <AppContainer>
        <LoadingContainer>
          <div>{t('common.loading')}</div>
        </LoadingContainer>
      </AppContainer>
    );
  }

  if (loading) {
    return (
      <AppContainer>
        <LoadingContainer>
          <div>{t('tree.loadingTree')}</div>
        </LoadingContainer>
      </AppContainer>
    );
  }

  if (error) {
    return (
      <AppContainer>
        <LoadingContainer>
          <div style={{ color: colors.error }}>{error}</div>
        </LoadingContainer>
      </AppContainer>
    );
  }

  return (
    <AppContainer>
      {/* Desktop: Fixed positioned buttons */}
      <DesktopOnlyButtons $isMobile={isMobile} $isLandscape={isLandscape}>
        <AuthButton />
        <PremiumButton
          user={user}
          isAuthenticated={isAuthenticated}
          translations={t}
          onFilterFavorites={handleFilterFavorites}
          onFilterProgress={handleFilterProgress}
          onPrintFavorites={handlePrintFavorites}
          onPrintProgress={handlePrintProgress}
          isFilteringFavorites={isFilteringFavorites}
          isFilteringProgress={isFilteringProgress}
        />
        <MusicPlayer />
        <LanguageSwitcher />
        <AboutMeButton
          autoOpen={showWelcomeModal && !isMobile}
          onAutoOpenHandled={() => setShowWelcomeModal(false)}
          isDevelopment={isDevelopment}
          isAdmin={isAdmin}
          toggleAdminMode={toggleAdminMode}
        />
      </DesktopOnlyButtons>

      {/* Position: fixed with its own desktop/mobile placement — must live
          outside DesktopOnlyButtons (display: none on mobile) */}
      {isAuthenticated && (
        <MySolutionsPopup allNodes={allNodes} onNavigateToNode={handleNavigateToNode} />
      )}

      <MainContent>
        <HeaderWrapper>
          <Header>
          {/* Mobile portrait: Music button in top-left corner */}
          <MobileMusicWrapper $isMobile={isMobile} $isLandscape={isLandscape}>
            <MusicPlayer />
          </MobileMusicWrapper>

          {/* Mobile portrait: Premium button in top-right corner */}
          <MobilePremiumWrapper $isMobile={isMobile} $isLandscape={isLandscape}>
            <PremiumButton
              user={user}
              isAuthenticated={isAuthenticated}
              translations={t}
              onFilterFavorites={handleFilterFavorites}
              onFilterProgress={handleFilterProgress}
              onPrintFavorites={handlePrintFavorites}
              onPrintProgress={handlePrintProgress}
              isFilteringFavorites={isFilteringFavorites}
              isFilteringProgress={isFilteringProgress}
            />
          </MobilePremiumWrapper>

          <HeaderContent>
            <TitleSection>
              <Title>Fuck Insomnia !</Title>
              <Subtitle>
                {t('tree.exploreAllPossibilities')}
                {isAdmin && <span style={{ color: colors.adminActive, marginLeft: '10px' }}>👑 {t('admin.adminMode')}</span>}
              </Subtitle>
              <CommunitySolutionsPopup
                allNodes={allNodes}
                onNavigateToNode={handleNavigateToNode}
              />
              {/* Admin only: the cards still waiting on an editorial decision. Renders nothing
                  for anyone else, and the endpoint checks is_admin server-side too. */}
              <FlaggedCardsPopup
                isAdmin={isAdmin}
                onNavigateToNode={handleNavigateToNode}
              />
            </TitleSection>

            {/* Mobile: Buttons in header */}
            <ButtonsContainer $isMobile={isMobile} $isLandscape={isLandscape} $visible={headerButtonsVisible}>
              <LanguageSwitcher />
              <AboutMeButton
                autoOpen={showWelcomeModal && isMobile}
                onAutoOpenHandled={() => setShowWelcomeModal(false)}
                isDevelopment={isDevelopment}
                isAdmin={isAdmin}
                toggleAdminMode={toggleAdminMode}
              />
              <AuthButton />
            </ButtonsContainer>

            {/* Collapsible part toggle (hides ONLY the buttons; title/subtitle always visible) */}
          </HeaderContent>
        </Header>

        {/* Collapsible part toggle handle (OUTSIDE header; hides ONLY the buttons) */}
        {isMobile && (
          <HeaderToggleButton
            onClick={() => {
              headerButtonsManuallyToggledRef.current = true;
              setHeaderButtonsVisible(v => !v);
            }}
            aria-label={headerButtonsVisible ? 'Hide header buttons' : 'Show header buttons'}
            title={headerButtonsVisible ? 'Hide buttons' : 'Show buttons'}
          >
            <HandleChevron direction={headerButtonsVisible ? 'up' : 'down'} />
          </HeaderToggleButton>
        )}
        </HeaderWrapper>
        
        <TreeContainer>
          <CameraTreeVisualization
            ref={cameraTreeRef}
            allNodes={
              (isFilteringFavorites || isFilteringProgress)
                ? filteredNodes
                : (isMobile && mobileFilteredNodes.length > 0)
                  ? mobileFilteredNodes
                  : calculateProgressiveNodes()
            }
            selectedNodeId={selectedNodeId}
            onNodeClick={handleNodeClick}
            onShowDetails={handleShowDetails}
            isAdmin={isAdmin}
            onEditNode={handleEditNode}
            onCreateChild={handleCreateChild}
            onDeleteNode={handleDeleteNode}
            onShowStats={handleShowStats}
            isFilteringFavorites={isFilteringFavorites}
            isFilteringProgress={isFilteringProgress}
            favoriteIds={favoriteIds}
            completedNodes={completedNodes}
            isAuthenticated={isAuthenticated}
            onProgressToggle={handleProgressToggle}
            isProgressiveMode={isProgressiveMode}
            expandedNodes={expandedNodes}
            onToggleExpansion={handleToggleNodeExpansion}
            allNodesUnfiltered={allNodes}
            isMobile={isMobile}
            translations={{
              searchPlaceholder: t('tree.searchPlaceholder'),
              searchCtrlF: t('tree.searchCtrlF'),
              overview: t('tree.overview'),
              focusOnSelected: t('tree.focusOnSelected'),
              wasdArrowKeys: t('tree.wasdArrowKeys'),
              mouseWheelZoom: t('tree.mouseWheelZoom'),
              dragPan: t('tree.dragPan'),
              ctrlFSearch: t('tree.ctrlFSearch'),
              loadingTree: t('tree.loadingTree'),
              treeNavigation: t('tree.treeNavigation'),
              showTreeNavigation: t('tree.showTreeNavigation'),
              hideTreeNavigation: t('tree.hideTreeNavigation'),
              level: t('tree.level')
            }}
          />
        </TreeContainer>
      </MainContent>

      <AnimatePresence>
        {selectedNode && (
          <NodeDetails
            node={selectedNode}
            onClose={() => setSelectedNode(null)}
            allNodes={allNodes}
          />
        )}
      </AnimatePresence>

      {/* World map modal for flip stats (admin only) */}
      <WorldMapModal
        isOpen={worldMapModal.isOpen}
        onClose={() => setWorldMapModal({ ...worldMapModal, isOpen: false })}
        nodeId={worldMapModal.nodeId}
        nodeTitle={worldMapModal.nodeTitle}
        type={worldMapModal.type}
      />

      {/* Modal d'édition admin */}
      <AnimatePresence>
        {showEditModal && (editingNode || isCreatingChild) && (
          <>
            <ModalOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowEditModal(false)}
            />
            <Modal
              initial={{ opacity: 0, scale: 0.8, x: '-50%', y: '-50%' }}
              animate={{ opacity: 1, scale: 1, x: '-50%', y: '-50%' }}
              exit={{ opacity: 0, scale: 0.8, x: '-50%', y: '-50%' }}
              style={{
                // Force le centrage parfait avec transform CSS
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                // S'assurer qu'il ne dépasse pas
                maxWidth: 'min(600px, 90vw)',
                maxHeight: 'min(85vh, calc(100vh - 40px))',
                // Force l'application de transform
                willChange: 'transform'
              }}
            >
              <h3>✏️ {t('admin.editNodeModal')}</h3>
              
              <Form onSubmit={handleSubmitEdit}>
                <FormGroup>
                  <Label>{t('admin.titleRequired')}</Label>
                  <Input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder={t('admin.nodeTitlePlaceholder')}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>{t('admin.descriptionLabel')}</Label>
                  <RichTextEditor
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder={t('admin.nodeDescriptionPlaceholder')}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>{t('admin.specialistInfoLabel')}</Label>
                  <Input
                    type="text"
                    value={formData.specialistInfo}
                    onChange={(e) => setFormData({...formData, specialistInfo: e.target.value})}
                    placeholder="Format: Spécialiste 1; Spécialiste 2; Spécialiste 3"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>{t('admin.shortDescriptionLabel')}</Label>
                  <Input
                    type="text"
                    value={formData.shortDescription}
                    onChange={(e) => setFormData({...formData, shortDescription: e.target.value})}
                    placeholder={t('admin.shortDescriptionPlaceholder')}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>{t('admin.trialDurationLabel')}</Label>
                  <Input
                    type="text"
                    value={formData.trialDuration}
                    onChange={(e) => setFormData({...formData, trialDuration: e.target.value})}
                    placeholder={t('admin.trialDurationPlaceholder')}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Sources (liens)</Label>
                  <TextArea
                    value={formData.sources}
                    onChange={(e) => setFormData({...formData, sources: e.target.value})}
                    placeholder="Format: (Titre du lien,https://url.com); (Autre titre,https://autre.com)"
                    rows="3"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>{t('admin.colorLabel')}</Label>
                  <Input
                    type="color"
                    value={formData.colorCode}
                    onChange={(e) => setFormData({...formData, colorCode: e.target.value})}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>{t('admin.iconLabel')}</Label>
                  <Input
                    type="text"
                    value={formData.iconName}
                    onChange={(e) => setFormData({...formData, iconName: e.target.value})}
                    placeholder={t('admin.iconPlaceholder')}
                  />
                </FormGroup>

                <FormGroup>
                  <Label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.aiGenerated}
                      onChange={(e) => setFormData({...formData, aiGenerated: e.target.checked})}
                      style={{ cursor: 'pointer' }}
                    />
                    {t('admin.aiGeneratedLabel') || 'AI-generated content'}
                  </Label>
                </FormGroup>

                <ModalActions>
                  <ActionButton type="button" danger onClick={() => setShowEditModal(false)}>
                    {t('common.cancel')}
                  </ActionButton>
                  <ActionButton type="submit">
                    💾 {t('common.save')}
                  </ActionButton>
                </ModalActions>
              </Form>
            </Modal>
          </>
        )}
      </AnimatePresence>
    </AppContainer>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
